import type { CapitalGlobalProposalAttachmentSlot } from '@/features/proposals/components/capital-global/utils/capital-global-proposal-attachment.util';
import {
  CAPITAL_GLOBAL_PROPOSAL_ATTACHMENT_ACCEPT,
  getCapitalGlobalProposalAttachmentDisplayFileName,
} from '@/features/proposals/components/capital-global/utils/capital-global-proposal-attachment.util';
import type {
  CapitalGlobalProposalAttachment,
  CapitalGlobalProposalAttachmentDocumentType,
} from '@/services/proposals/types';
import { Button, Spinner, Text } from '@noorden-insurtech/alba-design-system';
import { Calendar, Download, Trash2, Upload, UploadCloud } from 'lucide-react';
import { useMemo, useRef, type ChangeEvent, type DragEvent } from 'react';

type Props = {
  slots: CapitalGlobalProposalAttachmentSlot[];
  attachmentsByType: Partial<
    Record<
      CapitalGlobalProposalAttachmentDocumentType,
      CapitalGlobalProposalAttachment
    >
  >;
  isViewMode: boolean;
  uploadingDocumentType: CapitalGlobalProposalAttachmentDocumentType | null;
  deletingAttachmentId: string | null;
  showSignedProposalRequiredError: boolean;
  onFileSelect: (
    documentType: CapitalGlobalProposalAttachmentDocumentType,
    file: File
  ) => void;
  onRemove: (attachment: CapitalGlobalProposalAttachment) => void;
  onDownload: (attachment: CapitalGlobalProposalAttachment) => void;
};

type UploadedRow = {
  slot: CapitalGlobalProposalAttachmentSlot;
  attachment: CapitalGlobalProposalAttachment;
};

function formatUploadedAtLabel(uploadedAt: string): string {
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(uploadedAt));
}

function pickFirstFile(list: FileList | null): File | null {
  if (!list?.length) return null;
  return list[0] ?? null;
}

function buildUploadedRows(
  slots: CapitalGlobalProposalAttachmentSlot[],
  attachmentsByType: Props['attachmentsByType']
): UploadedRow[] {
  return slots.flatMap(slot => {
    const attachment = attachmentsByType[slot.documentType];
    if (!attachment) return [];
    return [{ slot, attachment }];
  });
}

type AttachmentsTableProps = {
  rows: UploadedRow[];
  isViewMode: boolean;
  uploadingDocumentType: CapitalGlobalProposalAttachmentDocumentType | null;
  deletingAttachmentId: string | null;
  onRemove: (attachment: CapitalGlobalProposalAttachment) => void;
  onDownload: (attachment: CapitalGlobalProposalAttachment) => void;
};

function CapitalGlobalProposalAttachmentsTable({
  rows,
  isViewMode,
  uploadingDocumentType,
  deletingAttachmentId,
  onRemove,
  onDownload,
}: AttachmentsTableProps) {
  return (
    <div className='overflow-x-auto rounded-3xl border border-alba-gray-300 bg-alba-white-500 shadow-[0px_4px_12px_rgba(15,22,30,0.08)]'>
      <div className='min-w-[min(100%,640px)]'>
        <div className='flex h-12 items-center gap-4 rounded-t-3xl bg-alba-gray-200 pl-14 pr-6 text-xs font-bold uppercase text-alba-black-500'>
          <div className='flex min-w-0 flex-1'>
            <span className='min-w-0 flex-1 text-left'>Documento</span>
            <span className='min-w-0 flex-1 text-center'>Enviado</span>
          </div>
          <span className='w-14 shrink-0 text-right'>Baixar</span>
          <span className='w-14 shrink-0 text-right'>Deletar</span>
        </div>
        {rows.map(({ slot, attachment }) => {
          const isDeleting = deletingAttachmentId === attachment.id;
          const isUploading = uploadingDocumentType === slot.documentType;
          const disabled = isViewMode || isUploading || isDeleting;
          const displayFileName =
            getCapitalGlobalProposalAttachmentDisplayFileName(
              attachment.fileName,
              attachment.keyPath
            );

          return (
            <div
              key={attachment.id}
              className='flex items-center gap-4 border-b border-alba-gray-200 px-6 py-4 last:border-b-0'
            >
              <div className='flex min-w-0 flex-1 items-center'>
                <div className='min-w-0 flex-1 pl-8'>
                  <Text className='truncate text-left text-sm font-medium text-alba-black-500'>
                    {slot.label}
                  </Text>
                  <Text className='truncate text-left text-sm text-alba-gray-600'>
                    {displayFileName}
                  </Text>
                </div>
                <div className='flex min-w-0 flex-1 items-center justify-center gap-2 text-sm text-alba-black-500'>
                  <Calendar className='h-4 w-4 shrink-0' strokeWidth={1.6} />
                  <span className='whitespace-nowrap'>
                    {formatUploadedAtLabel(attachment.uploadedAt)}
                  </span>
                </div>
              </div>
              <div className='flex w-[114px] shrink-0 items-center justify-between'>
                <button
                  type='button'
                  onClick={() => onDownload(attachment)}
                  disabled={isUploading}
                  className='rounded-lg p-1 text-alba-blue-500 hover:bg-alba-gray-100 disabled:cursor-not-allowed disabled:opacity-50'
                  aria-label={`Baixar ${displayFileName}`}
                >
                  <Download className='h-6 w-6' strokeWidth={1.6} />
                </button>
                <button
                  type='button'
                  disabled={disabled}
                  onClick={() => onRemove(attachment)}
                  className='rounded-lg p-1 text-alba-blue-500 hover:bg-alba-gray-100 disabled:cursor-not-allowed disabled:opacity-50'
                  aria-label={`Remover ${displayFileName}`}
                >
                  {isDeleting ? (
                    <Spinner size='sm' colorScheme='blue' />
                  ) : (
                    <Trash2 className='h-6 w-6' strokeWidth={1.6} />
                  )}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

type SlotProps = {
  slot: CapitalGlobalProposalAttachmentSlot;
  isViewMode: boolean;
  isUploading: boolean;
  showRequiredError: boolean;
  onFileSelect: (file: File) => void;
};

function CapitalGlobalProposalAttachmentUploadSlot({
  slot,
  isViewMode,
  isUploading,
  showRequiredError,
  onFileSelect,
}: SlotProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handlePick = () => inputRef.current?.click();

  const handleChangeInput = (e: ChangeEvent<HTMLInputElement>) => {
    const file = pickFirstFile(e.target.files);
    e.target.value = '';
    if (file) onFileSelect(file);
  };

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isViewMode || isUploading) return;
    const file = pickFirstFile(e.dataTransfer.files);
    if (file) onFileSelect(file);
  };

  const disabled = isViewMode || isUploading;

  return (
    <div className='flex flex-col gap-3'>
      <div className='flex flex-col gap-1'>
        <Text className='text-sm font-semibold text-alba-black-500'>
          {slot.label}
          {slot.required && (
            <span className='text-alba-error-red-500'> *</span>
          )}
        </Text>
        <Text className='text-sm text-alba-gray-600'>{slot.helperText}</Text>
        {showRequiredError && (
          <Text className='text-sm text-alba-error-red-500'>
            Proposta de Seguro Assinada é obrigatória.
          </Text>
        )}
      </div>

      <input
        ref={inputRef}
        type='file'
        accept={CAPITAL_GLOBAL_PROPOSAL_ATTACHMENT_ACCEPT}
        className='hidden'
        disabled={disabled}
        onChange={handleChangeInput}
      />

      <div
        className='flex flex-col items-center justify-center gap-4 rounded-xl border border-dashed border-alba-gray-400 bg-alba-gray-100/40 px-6 py-10'
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {isUploading ? (
          <Spinner size='lg' colorScheme='blue' />
        ) : (
          <>
            <UploadCloud
              className='h-10 w-10 text-alba-gray-500'
              strokeWidth={1.2}
            />
            <Text className='text-center text-sm text-alba-gray-600'>
              Arraste o arquivo aqui ou use o botão para selecionar.
            </Text>
            <Button
              type='button'
              variant='secondary'
              size='md'
              className='flex items-center gap-2'
              disabled={disabled}
              onClick={handlePick}
            >
              <Upload className='h-5 w-5' strokeWidth={1.6} />
              Adicionar arquivo
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

export function CapitalGlobalProposalAssinaturaImportSection({
  slots,
  attachmentsByType,
  isViewMode,
  uploadingDocumentType,
  deletingAttachmentId,
  showSignedProposalRequiredError,
  onFileSelect,
  onRemove,
  onDownload,
}: Props) {
  const uploadedRows = useMemo(
    () => buildUploadedRows(slots, attachmentsByType),
    [attachmentsByType, slots]
  );

  const emptySlots = useMemo(
    () => slots.filter(slot => !attachmentsByType[slot.documentType]),
    [attachmentsByType, slots]
  );

  return (
    <div className='flex flex-col gap-6'>
      <Text className='text-base font-bold text-alba-black-500'>
        Importação da Proposta e Documentos
      </Text>

      <div className='flex flex-col gap-2'>
        <Text className='text-sm text-alba-black-500'>
          Envie o documento da proposta devidamente assinada em formato PDF
          (.pdf). Se necessário, anexe documentos complementares.
        </Text>
        <Text className='text-sm text-alba-black-500'>
          Certifique-se de que o arquivo esteja legível, completo e contenha
          todas as assinaturas necessárias para evitar pendências.
        </Text>
      </div>

      {uploadedRows.length > 0 && (
        <CapitalGlobalProposalAttachmentsTable
          rows={uploadedRows}
          isViewMode={isViewMode}
          uploadingDocumentType={uploadingDocumentType}
          deletingAttachmentId={deletingAttachmentId}
          onRemove={onRemove}
          onDownload={onDownload}
        />
      )}

      {emptySlots.length > 0 && (
        <div className='flex flex-col gap-8'>
          {emptySlots.map(slot => (
            <CapitalGlobalProposalAttachmentUploadSlot
              key={slot.documentType}
              slot={slot}
              isViewMode={isViewMode}
              isUploading={uploadingDocumentType === slot.documentType}
              showRequiredError={
                showSignedProposalRequiredError &&
                slot.documentType === 'signed_proposal'
              }
              onFileSelect={file => onFileSelect(slot.documentType, file)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
